import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { listProjectBlueprints } from "@/features/blueprints/blueprintApi";
import { listProjects } from "@/features/projects/projectApi";
import type { Project } from "@/features/projects/projectTypes";
import {
  getProToolCatalog,
  listProToolResults,
} from "@/features/tools/proToolApi";
import type {
  Blueprint,
  ToolCatalogItem,
  ToolResultSummary,
} from "@/features/tools/proToolTypes";

export function useProToolPageData() {
  const { getToken, isLoaded } = useAuth();
  const [tools, setTools] = useState<ToolCatalogItem[]>([]);
  const [canUseProTools, setCanUseProTools] = useState(false);
  const [results, setResults] = useState<ToolResultSummary[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [generatedBlueprints, setGeneratedBlueprints] = useState<Blueprint[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refreshResults() {
    const token = await getToken();
    const resultResponse = await listProToolResults(token);
    setResults(resultResponse.results);
  }

  useEffect(() => {
    async function loadTools() {
      if (!isLoaded) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const catalog = await getProToolCatalog(token);
        setTools(catalog.tools);
        setCanUseProTools(catalog.canUseProTools);

        if (catalog.canUseProTools) {
          const [resultResponse, projectsResponse] = await Promise.all([
            listProToolResults(token),
            listProjects(token),
          ]);
          setResults(resultResponse.results);
          setProjects(projectsResponse.projects);

          const blueprintResponses = await Promise.all(
            projectsResponse.projects.map((project) =>
              listProjectBlueprints(token, project.id),
            ),
          );
          setGeneratedBlueprints(
            blueprintResponses
              .flatMap((response) => response.blueprints)
              .filter(
                (blueprint) =>
                  blueprint.status === "generated" && Boolean(blueprint.ai_output),
              )
              .sort(
                (a, b) =>
                  new Date(b.updated_at).getTime() -
                  new Date(a.updated_at).getTime(),
              ),
          );
        } else {
          setResults([]);
          setProjects([]);
          setGeneratedBlueprints([]);
        }
      } catch {
        setError("We could not load Pro tools right now. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }

    void loadTools();
  }, [getToken, isLoaded]);

  return {
    tools,
    canUseProTools,
    results,
    projects,
    generatedBlueprints,
    isLoading,
    error,
    getToken,
    refreshResults,
  };
}
